import React, { useCallback, useMemo, useRef, useEffect, useState } from 'react';
import { useTabsState } from './useTabsState';
import { Tab } from './Tab.mobile';
import { ContextMenu } from '../ContextMenu/ContextMenu.mobile';
import { FileTab, GestureState } from '@/types';
import '../../styles/tabs-mobile.css';

const VIEWPORT_TABS = 3;
const TAB_WIDTH = 100;
const ARROW_WIDTH = 44;

export const TabsBarMobile: React.FC = () => {
  const {
    tabs,
    pinnedTabs,
    unpinnedTabs,
    activeTabId,
    groups,
    addTab,
    closeTab,
    selectTab,
    pinTab,
    createGroup,
  } = useTabsState();

  const [scrollPosition, setScrollPosition] = useState(0);
  const [contextMenuTab, setContextMenuTab] = useState<string | null>(null);
  const [draggedTab, setDraggedTab] = useState<string | null>(null);
  const [gestureState, setGestureState] = useState<GestureState>({
    type: 'idle',
    startTime: 0,
    startPos: { x: 0, y: 0 },
    currentPos: { x: 0, y: 0 },
    isDragActive: false,
  });
  const [closeZoneActive, setCloseZoneActive] = useState(false);
  const [groupingHover, setGroupingHover] = useState(false);

  const tabContainerRef = useRef<HTMLDivElement>(null);
  const longPressTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const dragImageRef = useRef<HTMLDivElement>(null);

  // Calculate scroll bounds
  const maxScroll = Math.max(0, unpinnedTabs.length - VIEWPORT_TABS) * TAB_WIDTH;
  const visibleTabIndices = useMemo(() => {
    const startIndex = Math.floor(scrollPosition / TAB_WIDTH);
    return {
      start: startIndex,
      end: Math.min(startIndex + VIEWPORT_TABS, unpinnedTabs.length),
    };
  }, [scrollPosition, unpinnedTabs.length]);

  // Auto-scroll active tab into view
  useEffect(() => {
    const activeIndex = unpinnedTabs.findIndex(t => t.id === activeTabId);
    if (activeIndex !== -1) {
      const tabStart = activeIndex * TAB_WIDTH;
      const tabEnd = tabStart + TAB_WIDTH;
      const viewportStart = scrollPosition;
      const viewportEnd = scrollPosition + (VIEWPORT_TABS * TAB_WIDTH);

      if (tabStart < viewportStart) {
        setScrollPosition(Math.max(0, tabStart));
      } else if (tabEnd > viewportEnd) {
        setScrollPosition(Math.max(0, tabEnd - VIEWPORT_TABS * TAB_WIDTH));
      }
    }
  }, [activeTabId, unpinnedTabs, scrollPosition]);

  // Long-press gesture handling
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    setGestureState({
      type: 'idle',
      startTime: Date.now(),
      startPos: { x: touch.clientX, y: touch.clientY },
      currentPos: { x: touch.clientX, y: touch.clientY },
      isDragActive: false,
    });

    // Set long-press timeout (350ms)
    longPressTimeoutRef.current = setTimeout(() => {
      setGestureState(prev => ({
        ...prev,
        type: 'long-press',
        isDragActive: false,
      }));
      // Haptic feedback
      navigator.vibrate?.(50);
    }, 350);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    const currentPos = { x: touch.clientX, y: touch.clientY };
    const deltaX = Math.abs(currentPos.x - gestureState.startPos.x);
    const deltaY = Math.abs(currentPos.y - gestureState.startPos.y);
    const delta = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // 10px dead zone for long-press
    if (gestureState.type === 'idle' && delta > 10) {
      clearTimeout(longPressTimeoutRef.current);
      setGestureState(prev => ({
        ...prev,
        type: 'idle', // Cancel long-press
        currentPos,
      }));
      return;
    }

    if (gestureState.type === 'long-press') {
      // Determine drag direction
      const dragDirection = deltaX > deltaY ? 'horizontal' : 'vertical';

      if (!gestureState.isDragActive) {
        setGestureState(prev => ({
          ...prev,
          isDragActive: true,
          dragDirection: dragDirection as 'horizontal' | 'vertical',
          currentPos,
        }));
        // Haptic feedback on activation
        navigator.vibrate?.(50);
      } else {
        // Update current position
        setGestureState(prev => ({
          ...prev,
          currentPos,
        }));

        // Check for close zone if vertical drag
        if (gestureState.dragDirection === 'vertical' && currentPos.y > gestureState.startPos.y + 20) {
          setCloseZoneActive(true);
        }

        // Check for grouping hover if horizontal drag + holding
        if (gestureState.dragDirection === 'horizontal') {
          const hoveredTabId = getTabAtPosition(currentPos.x);
          setGroupingHover(hoveredTabId !== null && hoveredTabId !== draggedTab);
        }
      }
    }

    setGestureState(prev => ({
      ...prev,
      currentPos,
    }));
  }, [gestureState, draggedTab]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    clearTimeout(longPressTimeoutRef.current);

    if (gestureState.type === 'long-press' && gestureState.isDragActive) {
      if (gestureState.dragDirection === 'vertical') {
        // Check if dropped in close zone
        if (closeZoneActive) {
          closeTab(draggedTab!);
        }
      } else if (gestureState.dragDirection === 'horizontal') {
        // Reorder handling
        if (groupingHover) {
          // Create group
          createGroup([draggedTab!, getTabAtPosition(gestureState.currentPos.x)!]);
        }
      }
      setCloseZoneActive(false);
      setGroupingHover(false);
    } else if (gestureState.type === 'long-press' && !gestureState.isDragActive) {
      // Simple long-press - open context menu
      setContextMenuTab(draggedTab || getTabAtPosition(gestureState.startPos.x));
    }

    setGestureState({
      type: 'idle',
      startTime: 0,
      startPos: { x: 0, y: 0 },
      currentPos: { x: 0, y: 0 },
      isDragActive: false,
    });
    setDraggedTab(null);
  }, [gestureState, closeZoneActive, groupingHover, draggedTab, closeTab, createGroup]);

  const handleArrowClick = useCallback((direction: 'left' | 'right') => {
    const newPosition = direction === 'left'
      ? Math.max(0, scrollPosition - TAB_WIDTH)
      : Math.min(maxScroll, scrollPosition + TAB_WIDTH);
    setScrollPosition(newPosition);
  }, [scrollPosition, maxScroll]);

  const handleArrowHold = useCallback((direction: 'left' | 'right') => {
    const scrollSpeed = 200; // px/sec
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const distance = (scrollSpeed * elapsed) / 1000;
      const newPosition = direction === 'left'
        ? Math.max(0, scrollPosition - distance)
        : Math.min(maxScroll, scrollPosition + distance);

      setScrollPosition(newPosition);

      // Continue animation if still within bounds
      if ((direction === 'left' && newPosition > 0) || (direction === 'right' && newPosition < maxScroll)) {
        requestAnimationFrame(animate);
      }
    };

    animate();
  }, [scrollPosition, maxScroll]);

  const getTabAtPosition = (x: number): string | null => {
    const containerRect = tabContainerRef.current?.getBoundingClientRect();
    if (!containerRect) return null;

    // Calculate which tab based on x position
    const relativeX = x - containerRect.left - (2 * ARROW_WIDTH); // Account for arrows
    const tabIndex = Math.floor(relativeX / TAB_WIDTH);

    if (tabIndex >= 0 && tabIndex < unpinnedTabs.length) {
      return unpinnedTabs[visibleTabIndices.start + tabIndex]?.id || null;
    }
    return null;
  };

  const visibleUnpinnedTabs = unpinnedTabs.slice(
    visibleTabIndices.start,
    visibleTabIndices.end
  );

  return (
    <div className="tabs-bar-mobile">
      {/* Pinned tabs section */}
      {pinnedTabs.length > 0 && (
        <div className="tabs-pinned-mobile">
          {pinnedTabs.map(tab => (
            <Tab
              key={tab.id}
              tab={tab}
              isActive={activeTabId === tab.id}
              onTap={() => selectTab(tab.id)}
              onLongPress={() => setContextMenuTab(tab.id)}
              onDragStart={() => setDraggedTab(tab.id)}
              onDragEnd={() => setDraggedTab(null)}
            />
          ))}
          <div className="tabs-separator-mobile" />
        </div>
      )}

      {/* Scrollable unpinned tabs section */}
      <div className="tabs-scroll-container-mobile">
        {/* Left arrow */}
        {scrollPosition > 0 && (
          <button
            className="scroll-arrow-mobile left"
            onClick={() => handleArrowClick('left')}
            onMouseDown={() => handleArrowHold('left')}
          >
            ◀
          </button>
        )}

        {/* Tabs viewport */}
        <div
          className="tabs-viewport-mobile"
          ref={tabContainerRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {visibleUnpinnedTabs.map((tab, index) => (
            <Tab
              key={tab.id}
              tab={tab}
              isActive={activeTabId === tab.id}
              isDragging={draggedTab === tab.id}
              onTap={() => selectTab(tab.id)}
              onLongPress={() => setContextMenuTab(tab.id)}
              onDragStart={() => setDraggedTab(tab.id)}
              onDragEnd={() => setDraggedTab(null)}
            />
          ))}
        </div>

        {/* Right arrow */}
        {scrollPosition < maxScroll && (
          <button
            className="scroll-arrow-mobile right"
            onClick={() => handleArrowClick('right')}
            onMouseDown={() => handleArrowHold('right')}
          >
            ▶
          </button>
        )}
      </div>

      {/* Close zone */}
      {closeZoneActive && (
        <div className={`close-zone-mobile ${closeZoneActive ? 'active' : ''}`}>
          <div className={`close-zone-button ${closeZoneActive ? 'hover' : ''}`}>
            Close
          </div>
        </div>
      )}

      {/* Context menu */}
      {contextMenuTab && (
        <ContextMenu
          tab={tabs.find(t => t.id === contextMenuTab)!}
          onClose={() => setContextMenuTab(null)}
          onCloseTab={() => {
            closeTab(contextMenuTab);
            setContextMenuTab(null);
          }}
          onPin={() => {
            pinTab(contextMenuTab);
            setContextMenuTab(null);
          }}
        />
      )}
    </div>
  );
};